import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { KeyboardService } from "common/keyboard.service";
import { ScrollService } from "common/routing/scroll.service";
import { MainService } from "core/main.service";
import { TranslateChoiceService } from "core/translate/translate-choice.service";
import { FileService } from "files/files.service";
import { combineLatest, fromEvent, merge, Observable, of, Subject, switchMap } from "rxjs";
import { catchError, debounceTime, filter, first, map, skip, takeUntil } from "rxjs/operators";
import { OverviewResults, SearchService, Suggestion, TypeAggregation } from "search/search.service";
import { ApplicationContextRepository } from "core/application-context.repository";

type WorkspaceView = stages.workspace.application.WorkspaceView;

enum KEY_CODE {
	ARROW_UP = 38,
	ARROW_DOWN = 40,
	ENTER = 13,
	ESCAPE = 27,
}

enum Subsection {
	Suggestions,
	MoreLink,
	TypeAggregations,
	ExtendRestrictLink,
}

@Component({
	selector: "stages-global-search-box",
	templateUrl: "search.component.html",
})
export class GlobalSearchBoxComponent implements OnInit, OnDestroy, AfterViewInit {
	@ViewChild("searchInput") searchInput!: ElementRef;

	isMobileView: boolean = false;
	isSearchOpen: boolean = false;

	private destroy$ = new Subject<boolean>();
	readonly MIN_TERM_LENGTH = 1;

	appSearchScope$!: Observable<string>;
	applicationContext$!: Observable<unknown>;

	flyoutVisible = false;
	downloadRequest = false;
	clickListener?: () => void;
	onKeyDown!: (event: KeyboardEvent) => void;

	index = -1;
	currentIndex = -1;
	readonly Subsection = Subsection;

	searchTerm!: string;
	searchEngineAvailable = true;
	searching = false;
	overviewResults?: OverviewResults | null;
	workspaces!: WorkspaceView;
	workspaceView$: Observable<stages.workspace.application.WorkspaceView> | undefined;
	overviewResult$: Observable<OverviewResults> | null | undefined;
	isSearchNeeded: boolean = false;

	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private mainService: MainService,
		private searchService: SearchService,
		private scrollService: ScrollService,
		private keyboardService: KeyboardService,
		private translateChoiceService: TranslateChoiceService,
		private renderer: Renderer2,
		private applicationContextRepository: ApplicationContextRepository,
		private fileService: FileService,
		translateService: TranslateService,
	) {
		translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => (this.overviewResults = null));
	}

	ngOnInit(): void {
		combineLatest([
			this.activatedRoute.paramMap,
			this.applicationContextRepository.searchScope$,
			this.mainService.workspaceView$,
		])
			.pipe(
				map(([paramMap, appSearchScope, workspaceView]) => {
					this.initializeSearchService(paramMap.get("processVersion")!, appSearchScope);
					return workspaceView;
				}),
				takeUntil(this.destroy$),
			)
			.subscribe((workspaceView) => {
				this.workspaces = workspaceView;
			});

		// for what ??? maybe implement into overview observable?!
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				takeUntil(this.destroy$),
			)
			.subscribe(() => {
				const urlTree = this.router.parseUrl(this.router.url);
				const filterMode = urlTree.root.children.primary.children.dialog;
				if (filterMode) {
					const segment = urlTree.root.children.primary.children.dialog.segments[0];
					if (segment.path === "search") {
						this.searchService.setCategories(segment.parameterMap);
						this.searchService.setCategories(segment.parameterMap);
						const scopeFromRouteChild = segment.parameterMap.get("scope");
						if (scopeFromRouteChild) {
							this.searchService.setSearchScope(scopeFromRouteChild);
						}
						const selectedPVToSearchFor = segment.parameterMap.get("pv");
						if (selectedPVToSearchFor) {
							this.searchService.setProcessVersionToSearchFor(selectedPVToSearchFor);
						}

						this.searchTerm = segment.parameterMap.get("term") || "";

						this.searchInput.nativeElement.value = this.searchTerm;
					}
				}
			});

		this.mainService.applicationState.isMobileView.pipe(takeUntil(this.destroy$)).subscribe((isMobileView: boolean) => {
			this.isMobileView = isMobileView;
		});

		this.mainService.applicationState.isSearchOpen.pipe(takeUntil(this.destroy$)).subscribe((isSearchOpen: boolean) => {
			this.isSearchOpen = isSearchOpen;
			if (isSearchOpen && this.isMobileView) {
				this.searchInput.nativeElement.focus();
			}
		});
	}

	ngAfterViewInit(): void {
		const firstSearchterm$ = fromEvent<KeyboardEvent>(this.searchInput.nativeElement, "keyup").pipe(
			filter((event) => this.keyboardService.isAlphanumericKey(event)),
			first(),
		);

		const debouncedSearchterm$ = fromEvent<KeyboardEvent>(this.searchInput.nativeElement, "keyup").pipe(
			filter((event) => this.keyboardService.isAlphanumericKey(event)),
			skip(1),
			debounceTime(250), // delay for sending an overview request with the searchterm to the backend
		);

		merge(firstSearchterm$, debouncedSearchterm$)
			.pipe(
				switchMap(() => {
					if (this.isSearchNeeded) {
						this.searching = true;
						return this.getOverview(this.searchInput.nativeElement.value).pipe(
							catchError((error) => {
								this.searchEngineAvailable = false;
								return of<OverviewResults>({
									type_aggregations: [],
									suggestions: [],
									other_workspaces_count: [],
									error: error,
								});
							}),
						);
					}
					return of<OverviewResults>({
						type_aggregations: [],
						suggestions: [],
						other_workspaces_count: [],
						error: "Search could not be executed!",
					});
				}),
				takeUntil(this.destroy$),
			)
			.subscribe((overviewResults) => {
				this.overviewResults = overviewResults as OverviewResults;
				this.showFlyout();
				this.searching = false;
			});
	}

	initializeSearchService(pv: string, appSearchScope: string): void {
		this.searchService.setDefaultProcessVersionToSearchFor(pv);
		this.searchService.setProcessVersionToSearchFor(pv);

		const convertedAppSearchScope = convertSearchScope(appSearchScope);
		this.searchService.setDefaultSearchScope(convertedAppSearchScope);
		this.searchService.setSearchScope(convertedAppSearchScope);
	}

	ngOnDestroy(): void {
		if (this.clickListener) {
			this.clickListener();
		}

		this.destroy$.next(true);
		this.destroy$.complete();
	}

	private enterKeyOpensResultPage(): boolean {
		return this.index === -1 && this.isTermAvailable() && this.searchEngineAvailable;
	}

	private isTermAvailable(): boolean {
		if (!this.searchTerm) {
			return false;
		}
		return this.searchTerm.length >= this.MIN_TERM_LENGTH;
	}

	closeSearch(): void {
		this.hideFlyoutAndResetIndex();
		this.mainService.applicationState.closeSearch();
	}

	openResults(searchScope: string, categories?: string[]): void {
		this.router.navigate(
			[
				{
					outlets: {
						dialog: [
							"search",
							{
								term: this.searchTerm,
								scope: searchScope,
								pv: this.searchService.getProcessVersionToSearchFor(),
								categories: categories ?? this.searchService.getCategories(),
								rpage: 1,
							},
						],
					},
				},
			],
			{
				relativeTo: this.activatedRoute,
			},
		);
		this.hideFlyoutAndResetIndex();
	}

	private showFlyout(): void {
		/* We display the flyout only when we have results or the search engine is not available. */
		if (!this.flyoutVisible && this.isSearchNeeded && (this.overviewResults || !this.searchEngineAvailable)) {
			this.index = -1;
			this.clickListener = this.renderer.listen("document", "click", () => this.hideFlyoutAndResetIndex());
			if (!this.flyoutVisible) {
				this.scrollService.disableWindowScroll();
			}
			this.flyoutVisible = true;
		}
	}

	getOverview(searchTermInput: string): Observable<OverviewResults> {
		this.searchEngineAvailable = true;
		return this.searchService.getOverview(
			this.workspaces.currentWorkspace.id,
			this.searchService.getProcessVersionToSearchFor(),
			searchTermInput,
			this.searchService.getSearchScope(),
		);
	}

	onKeyUp(event: KeyboardEvent): void {
		this.isSearchNeeded = false;
		this.searching = false;
		const key = event.which || event.keyCode;

		switch (key) {
			case KEY_CODE.ESCAPE: {
				this.closeSearch();
				break;
			}
			case KEY_CODE.ENTER: {
				if (this.enterKeyOpensResultPage()) {
					this.openResults(this.searchService.getSearchScope());
				} else if (this.searchEngineAvailable && this.isTermAvailable()) {
					this.openItem(this.workspaces);
				}
				this.closeSearch();
				break;
			}
			case KEY_CODE.ARROW_DOWN: {
				this.index = ++this.index % this.getItemCount();
				break;
			}
			case KEY_CODE.ARROW_UP: {
				this.index = this.index > 0 ? --this.index : this.getItemCount() - 1;
				break;
			}
			default: {
				if (this.searchTerm === "") {
					this.searching = false;
					this.hideFlyoutAndResetIndex();
					break;
				} else {
					this.isSearchNeeded = true;
					break;
				}
			}
		}
	}

	private hideFlyoutAndResetIndex(): void {
		this.index = -1;
		this.scrollService.enableWindowScroll();
		this.flyoutVisible = false;
		if (this.clickListener) {
			this.clickListener();
			this.clickListener = undefined;
		}
	}

	preventClosingFlyout($event: UIEvent): void {
		if ($event.preventDefault) {
			$event.preventDefault();
			$event.stopPropagation();
		}
	}

	isFlyoutVisible(): boolean {
		const results = this.overviewResults;
		/* The search engine is available and the results have been loaded/fetched. */
		const resultsLoaded =
			this.searchEngineAvailable &&
			this.flyoutVisible &&
			results &&
			!results.error &&
			results.type_aggregations.length + results.suggestions.length + results.other_workspaces_count[0].doc_count > 0;
		/* The search engine is not available. */
		const searchNotAvailable = !this.searchEngineAvailable && this.flyoutVisible;
		return resultsLoaded || searchNotAvailable;
	}

	private getItemCount(): number {
		if (this.overviewResults) {
			const nSuggestions = this.overviewResults.suggestions.length;
			return (
				this.overviewResults.type_aggregations.length +
				this.overviewResults.other_workspaces_count.length +
				(nSuggestions > 0 ? nSuggestions + 1 : 0)
			);
		}
		return -1;
	}

	isFile(suggestion: Suggestion): boolean {
		return suggestion.type.ident === "file";
	}

	getFileIconClass(suggestion: Suggestion): string {
		if (suggestion.fileType) {
			return "ico-ft-" + suggestion.fileType;
		}
		return "ico-" + suggestion.type.subtypeIdent;
	}

	getFileUrl(suggestion: Suggestion, workspaceId: string): string {
		return "file/" + suggestion.id + "?workspaceId=" + workspaceId;
	}

	hasResults(): boolean {
		if (!this.overviewResults) {
			return false;
		}
		return this.overviewResults.suggestions.length > 0 || this.overviewResults.type_aggregations.length > 0;
	}

	hasResultsInOtherWorkspaces(): boolean {
		const otherWorkspaceCount =
			this.overviewResults && !this.overviewResults.error
				? this.overviewResults.other_workspaces_count[0].doc_count
				: 0;
		return otherWorkspaceCount > 0;
	}

	getReverseSearchLabel(): string {
		return this.translateChoiceService.getTranslation(
			{
				currentOrAll: this.searchService.getSearchInvertedScope() === "all" ? "current" : "all",
			},
			"search.flyout.search.{currentOrAll}",
		);
	}

	get searchScope(): string {
		return this.searchService.getSearchScope();
	}

	buildFileContainerRouterLink(workspaces: WorkspaceView, fileSearchSuggestion: Suggestion): unknown[] {
		return this.buildRouterLink(workspaces, fileSearchSuggestion, [
			"process",
			fileSearchSuggestion.containerElementTypeIdent!,
			fileSearchSuggestion.containerElementIdentity!,
		]);
	}

	buildElementRouterLink(workspaces: WorkspaceView, elementSearchSuggestion: Suggestion): unknown[] {
		if (elementSearchSuggestion.type.ident === "tailoring") {
			return this.buildRouterLink(workspaces, elementSearchSuggestion, [
				"management",
				"tailoring",
				elementSearchSuggestion.identity,
			]);
		} else if (elementSearchSuggestion.type.ident === "description") {
			return this.buildRouterLink(workspaces, elementSearchSuggestion, [
				"management",
				"descriptions",
				elementSearchSuggestion.identity,
			]);
		} else if (elementSearchSuggestion.type.ident.startsWith("enactment")) {
			// type ident is enactmentactivator or enactmentscript but url segment needs to be activator or script
			return this.buildRouterLink(workspaces, elementSearchSuggestion, [
				"management",
				"enactment",
				elementSearchSuggestion.type.ident.slice(9),
				elementSearchSuggestion.identity,
			]);
		}
		return this.buildRouterLink(workspaces, elementSearchSuggestion, [
			"process",
			elementSearchSuggestion.type.ident,
			elementSearchSuggestion.identity,
		]);
	}

	private buildRouterLink(workspaces: WorkspaceView, suggestion: Suggestion, commands: unknown[]): unknown[] {
		const preserve = this.searchService.preserveSecondaryWorkspaceInSearchResultNavigation(
			workspaces,
			suggestion.workspaceId,
			suggestion.processId,
		);

		return [
			"/",
			"workspace",
			suggestion.workspaceId,
			suggestion.processVersionIdentifier,
			...this.mainService.getSecondaryRouterPart(preserve),
			{ outlets: { primary: commands, dialog: null } },
		];
	}

	isSelected(results: OverviewResults, subsection: Subsection, indexWithinSubsection: number): boolean {
		if (!results) {
			return false;
		}
		return this.index === getIndex(results, subsection, indexWithinSubsection);
	}

	onKeyUpForFileSearchResult(event: KeyboardEvent, suggestion: Suggestion, workspaceId: string): void {
		if (this.keyboardService.isReturnKeyPressed(event)) {
			this.downloadFileFromSearchSuggestion(suggestion, workspaceId);
		}
	}

	downloadFileFromSearchSuggestion(suggestion: Suggestion, workspaceId: string): void {
		this.fileService.download(
			this.activatedRoute,
			suggestion.id,
			workspaceId,
			this.activatedRoute.snapshot.paramMap.get("ProcessVersion")!,
			null,
			false,
		);
	}

	openItem(workspaces: WorkspaceView): void {
		// Validate results and index
		if (!this.overviewResults) {
			throw new Error("No results - cannot open item");
		}
		const currentIndex = this.index;
		this.hideFlyoutAndResetIndex();

		const { suggestions, type_aggregations } = this.overviewResults;

		// Handle suggestion results
		if (currentIndex < suggestions.length) {
			const suggestion = suggestions[currentIndex];
			if (!suggestion) {
				return;
			}

			suggestion.type.ident === "file"
				? this.handleFileResult(suggestion, workspaces)
				: this.handleElementResult(suggestion, workspaces);

			return;
		}

		if (suggestions.length > 0 && currentIndex === suggestions.length) {
			this.openResults(this.searchService.getSearchScope());
			return;
		}

		// Handle type aggregations
		const aggregationIndex = suggestions.length > 0 ? currentIndex - suggestions.length - 1 : currentIndex;
		if (aggregationIndex < type_aggregations.length) {
			this.handleTypeAggregationResult(aggregationIndex, type_aggregations);
			return;
		}

		// Handle inverted scope as fallback
		this.handleInvertedScopeResult();
	}

	// Private helper methods

	private handleFileResult(suggestion: Suggestion, workspaces: WorkspaceView): void {
		this.searchTerm = "";
		this.downloadFileFromSearchSuggestion(suggestion, workspaces.currentWorkspace.id);
	}

	private handleElementResult(suggestion: Suggestion, workspaces: WorkspaceView): void {
		this.router.navigate(this.buildElementRouterLink(workspaces, suggestion));
	}

	private handleTypeAggregationResult(index: number, typeAggregations: TypeAggregation[]): void {
		this.openResults(this.searchService.getSearchScope(), [typeAggregations[index].key]);
	}

	private handleInvertedScopeResult(): void {
		this.openResults(this.searchService.getSearchInvertedScope());
	}

	onFocus(): void {
		this.flyoutVisible = this.isFlyoutVisible();
		if (this.isTermAvailable()) {
			// we restored the term, but there are no cached results
			this.showFlyout();
		}
	}
}

function convertSearchScope(searchScope: string): string {
	switch (searchScope) {
		case "ALL_WORKSPACES": {
			return "all";
		}
		case "CURRENT_WORKSPACE": {
			return "local";
		}
		case "FAVORITE_WORKSPACES": {
			return "my";
		}
		case "RECOMMENDED_WORKSPACES": {
			return "recommended";
		}
		default: {
			throw new Error("Unknown search scope: " + searchScope);
		}
	}
}

function getIndex(results: OverviewResults, subsection: Subsection, indexWithinSubsection: number): number {
	switch (subsection) {
		case Subsection.Suggestions: {
			return indexWithinSubsection;
		}
		case Subsection.MoreLink: {
			return results.suggestions.length;
		}
		// "More results" link appears only when there are suggestions
		case Subsection.TypeAggregations: {
			return results.suggestions.length
				? indexWithinSubsection + results.suggestions.length + 1
				: indexWithinSubsection;
		}
		case Subsection.ExtendRestrictLink: {
			return results.suggestions.length
				? indexWithinSubsection + results.type_aggregations.length + results.suggestions.length + 1
				: indexWithinSubsection + results.type_aggregations.length;
		}
		default: {
			throw new Error(`unexpected subsection: ${subsection}`);
		}
	}
}
